import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { storage } from '../storage.js';
import { VividWordmark } from '../primitives.jsx';
import { Button, Spinner } from '../components/v24/index.jsx';

// Logout = revoke the refresh token, clear local state, then say so.
// Mounting this page at /logout makes it possible for ERP/Grid to do
// `window.location.href = AUTH_URL + '/logout'` and end up on the auth
// domain. It used to redirect straight to /login whatever happened; it now
// ends on a confirmed state, or says when the server could not be told
// (AUTH-507). The sign-in on this device is removed either way.
export default function LogoutPage() {
  const [status, setStatus] = useState('working'); // working | done | error
  const started = useRef(false);

  const signOut = useCallback(() => {
    setStatus('working');
    const refresh = storage.getRefresh();
    // VERP-385 — always call /auth/logout, not just when a refresh token
    // is sitting in this app's own localStorage. Since VERP-172 the real
    // session anchor is the httpOnly v360_refresh cookie, which every
    // ERP/Grid session now relies on exclusively and this page never had
    // a copy of in the first place — so `refresh` was always falsy and
    // the revoke call (and therefore the server-side session delete +
    // Set-Cookie clear) was silently skipped for every real user. Same-
    // origin fetch sends the cookie automatically; the backend already
    // treats a missing body token as "read it from the cookie instead"
    // and clears the cookie unconditionally either way, so this is safe
    // to call even for a session that somehow has no token at all.
    return api.logout(refresh)
      .then(() => 'done')
      .catch(() => 'error')
      .then((outcome) => {
        // Local state goes either way: keeping a token here because the
        // server was unreachable would leave the user signed in.
        storage.clearAuth();
        storage.clearRouting();
        setStatus(outcome);
      });
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    signOut();
  }, [signOut]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
      <VividWordmark />
      {status === 'working' && (
        <div role="status" className="mt-8 flex items-center gap-3 text-[14px] text-stone-500">
          <span aria-hidden="true"><Spinner size={18} label="Signing you out" /></span>
          <span>Signing you out...</span>
        </div>
      )}
      {status === 'done' && (
        <div className="mt-8 text-center">
          <h1 className="text-[24px] font-bold tracking-tight text-ink">You are signed out</h1>
          <p className="mt-2 text-[14px] text-stone-500">Your session on this device has ended.</p>
          <Link to="/login" className="btn-primary mt-6 inline-flex">Sign in again</Link>
        </div>
      )}
      {status === 'error' && (
        <div role="alert" className="mt-8 max-w-[420px] text-center">
          <p className="text-[15px] text-ink">
            We removed your sign-in from this device but could not confirm that the server ended
            the session. Try again to be sure.
          </p>
          <div className="mt-5 flex items-center justify-center gap-4">
            <Button onClick={signOut}>Try again</Button>
            <Link to="/login" className="text-[13px] font-medium text-ink hover:underline">
              Sign in again
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
